import { FunctionComponent, useCallback, useMemo } from 'react';
import { UrlObject } from '../../../hooks/useRouterState';
import { ParsedResults, SearchPageProps } from '../../../pages/courses/[[...parts]]';
import { WebCourseSearchFilters } from '../../../services/search/course/useCourseSearch';
import LocationFacetFilter from './LocationFacetFilter';
import styles from './styles.module.scss';
import MinimumRatingFilter from './MinimumRatingFilter';
import {
    SearchIndexAutoComplete,
    SearchIndexCourseFilters,
    SearchIndexEducatorFilters,
} from '@shared/types';
import { GeneralSearch } from '../../home/SearchBar/PrimaryQuerySelect';
import { DeliveryFilterSection } from './DeliveryFilterSection';

export type SearchPageFiltersProps = {
    filters: SearchPageProps['query']['filters'];
    setFilters: (newFilters: SearchPageProps['query']['filters']) => void;
    getFilterLink: (newFilters: SearchPageProps['query']['filters']) => UrlObject;
    results: {
        courses: ParsedResults['courses'];
        educators: ParsedResults['educators'];
    };
    providers_only: boolean;
    search: string;
    setSearch: (newSearch: SearchIndexAutoComplete | GeneralSearch) => void;
    hasInvalidLocation: boolean;
};

const useCourseFilterOnChange = <
    Key extends keyof SearchPageFiltersProps['filters']['courseFilters'],
>(
    filters: SearchPageFiltersProps['filters'],
    setFilters: SearchPageFiltersProps['setFilters'],
    filterKey: Key
): ((newValue: SearchPageFiltersProps['filters']['courseFilters'][Key]) => void) => {
    const onChange = useCallback(
        (newValue: SearchPageFiltersProps['filters']['courseFilters'][Key]) => {
            setFilters({
                ...filters,
                courseFilters: {
                    ...filters.courseFilters,
                    [filterKey]: newValue,
                },
            });
        },
        [filters, setFilters, filterKey]
    );
    return onChange;
};

const useGetCourseFilterLink = <
    Key extends keyof SearchPageFiltersProps['filters']['courseFilters'],
>(
    filters: SearchPageFiltersProps['filters'],
    getFilterLink: SearchPageFiltersProps['getFilterLink'],
    filterKey: Key
): ((newValue: SearchPageFiltersProps['filters']['courseFilters'][Key]) => UrlObject) => {
    const getLink = useCallback(
        (newValue: SearchPageFiltersProps['filters']['courseFilters'][Key]) => {
            return getFilterLink({
                ...filters,
                courseFilters: {
                    ...filters.courseFilters,
                    [filterKey]: newValue,
                },
            });
        },
        [filters, getFilterLink, filterKey]
    );
    return getLink;
};

const SearchPageFilters: FunctionComponent<SearchPageFiltersProps> = ({
    filters,
    setFilters,
    getFilterLink,
    results,
    providers_only,
    search,
    setSearch,
    hasInvalidLocation,
}) => {
    const courseDeliveryFilterOnChange = useCourseFilterOnChange(filters, setFilters, 'delivery');
    const getCourseDeliveryFilterLink = useGetCourseFilterLink(filters, getFilterLink, 'delivery');

    const locationFilterOnChange = useCallback(
        (newValue: WebCourseSearchFilters['location']) => {
            setFilters({
                ...filters,
                courseFilters: {
                    ...filters.courseFilters,
                    location: newValue,
                },
                educatorFilters: {
                    ...filters.educatorFilters,
                    location: newValue,
                },
            });
        },
        [filters, setFilters]
    );

    const minimumCourseRatingFilterOnChange = useCallback(
        (newValue: SearchIndexCourseFilters['minimum_rating']) => {
            setFilters({
                ...filters,
                courseFilters: {
                    ...filters.courseFilters,
                    minimum_rating: newValue,
                },
                educatorFilters: filters.educatorFilters,
            });
        },
        [filters, setFilters]
    );

    const minimumEducatorRatingFilterOnChange = useCallback(
        (newValue: SearchIndexEducatorFilters['minimum_rating']) => {
            setFilters({
                ...filters,
                courseFilters: filters.courseFilters,
                educatorFilters: {
                    ...filters.educatorFilters,
                    minimum_rating: newValue,
                },
            });
        },
        [filters, setFilters]
    );

    if (providers_only) {
        return (
            <div className={styles.filterContainer}>
                <LocationFacetFilter
                    value={filters.courseFilters.location}
                    onChange={locationFilterOnChange}
                    hasInvalidLocation={hasInvalidLocation}
                />
                <MinimumRatingFilter
                    value={filters.educatorFilters.minimum_rating}
                    onChange={minimumEducatorRatingFilterOnChange}
                />
            </div>
        );
    }

    return (
        <div className={styles.filterContainer}>
            <DeliveryFilterSection
                value={filters.courseFilters.delivery}
                onChange={courseDeliveryFilterOnChange}
                getFilterLink={getCourseDeliveryFilterLink}
            />
            <LocationFacetFilter
                value={filters.courseFilters.location}
                onChange={locationFilterOnChange}
                hasInvalidLocation={hasInvalidLocation}
            />
            <MinimumRatingFilter
                value={filters.courseFilters.minimum_rating}
                onChange={minimumCourseRatingFilterOnChange}
            />
        </div>
    );
};

export const useNonEmptyFacets = <IDType extends string>(
    facets: Array<{ _id: IDType | null; count: number }> | undefined
): Array<{ _id: IDType; count: number }> => {
    const nonEmptyFacets = useMemo(() => {
        if (!facets) {
            return [];
        }
        return removeEmptyFacets(facets);
    }, [facets]);
    return nonEmptyFacets;
};

const removeEmptyFacets = <InternalType extends string>(
    facets: Array<{ _id: InternalType | null; count: number }>
): Array<{ _id: InternalType; count: number }> => {
    return facets.filter((facet): facet is { _id: InternalType; count: number } => {
        return facet._id !== null && facet._id !== '';
    });
};

export default SearchPageFilters;
